import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchResult } from './types';
import './SearchResultCard.scss';
import '../MessagesModal/MessagesModal.scss';
import OffMarketSellIcon from '../FeedEvents/OffMarketSellIcon';
import OffMarketBuyIcon from '../FeedEvents/OffMarketBuyIcon';
import MiniMenuIcon from '../FeedEvents/MiniMenuIcon';
import TrashIcon from '../BuildingModal/Comments/TrashIcon';
import OffMarketBuyAndSellDetailsModal, { PropertyDetails } from '../BuildingModal/OffMarketBuyAndSellDetailsModal';
import { getCookie } from '../utils/utils';
import { Trans } from 'react-i18next';

interface SearchResultCardProps {
  result: SearchResult;
  onContact: () => void;
  onViewOnMap: () => void;
  setShowRemoveListingDialog: (listingId: string | null) => void;
  fetchLandingPageFeed: () => void;
  handleRefreshFeedClick: () => void;
  performSearch: (loadMore?: boolean) => void;
}

const SearchResultCard: React.FC<SearchResultCardProps> = ({
  result,
  onContact,
  onViewOnMap,
  setShowRemoveListingDialog,
  fetchLandingPageFeed,
  handleRefreshFeedClick,
  performSearch
}) => {
  const { t } = useTranslation();
  const isForSale = result.could_sell;
  const hasNoImages = result.images && result.images.length === 0;
  const hasNoDescription = !result.property_description;
  const hasNoFeatures = !result.property_has_elevator && !result.property_has_balcony && !result.property_renovation_status;
  const hasNoSpecs = !result.property_size && !result.property_rooms && !result.property_floor;
  const hasNoDetailsOrImages = hasNoImages && hasNoDescription && hasNoFeatures && hasNoSpecs;

  // State for menu and modals
  const [showEventControlsMenu, setShowEventControlsMenu] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editModalLoading, setEditModalLoading] = useState(false);
  const [eventData, setEventData] = useState<PropertyDetails | null>(null);
  const editEventRef = useRef<HTMLButtonElement | null>(null);
  const eventControlsMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const eventControlsMenuRef = useRef<HTMLDivElement | null>(null);

  const getRenovationStatusLabel = (status: string) => {
    switch (status) {
      case 'recently_renovated':
        return t('exploreListings.recentlyRenovated');
      case 'good_condition':
        return t('exploreListings.goodCondition');
      case 'needs_renovation':
        return t('exploreListings.needsRenovation');
      default:
        return status;
    }
  };

  // Handler for fetching and opening edit modal
  const handleEditClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setEditModalLoading(true);

      // Fetch the current willingness data from the backend
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/off-market-willingness/${result._id}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch willingness data');
      }

      const data = await response.json();

      // Convert the backend data to PropertyDetails format
      const propertyDetails: PropertyDetails = {
        description: data.property_description,
        size: data.property_size,
        rooms: data.property_rooms,
        floor: data.property_floor,
        hasElevator: data.property_has_elevator,
        hasBalcony: data.property_has_balcony,
        renovationStatus: data.property_renovation_status,
        images: data.images
      };

      setEventData(propertyDetails);
      setIsEditModalOpen(true);
      setShowEventControlsMenu(false); // Hide the menu
    } catch (error) {
      console.error('Error fetching willingness data:', error);
      alert(t('common.error', 'An error occurred. Please try again.'));
    } finally {
      setEditModalLoading(false);
    }
  };

  // Handler for updating the willingness event
  const handleEditConfirm = async (propertyDetails?: PropertyDetails) => {
    try {
      setEditModalLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/off-market-willingness/${result._id}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
          body: JSON.stringify({
            property_description: propertyDetails?.description,
            // Send explicit nulls for cleared fields so backend can clear stored values
            property_size: propertyDetails?.size ?? null,
            property_rooms: propertyDetails?.rooms === '' ? null : propertyDetails?.rooms,
            property_floor: propertyDetails?.floor ?? null,
            property_has_elevator: propertyDetails?.hasElevator,
            property_has_balcony: propertyDetails?.hasBalcony,
            property_renovation_status: propertyDetails?.renovationStatus === '' ? null : propertyDetails?.renovationStatus,
            property_images: propertyDetails?.images
          }),
        }
      );

      if (!response.ok) {
        try {
          const err = await response.json();
          if (err && err.error_code === 'SIZE_MUST_BE_GREATER_THAN_ZERO') {
            alert(t('building.offMarket.modal.sizeMustBeGreaterThanZero'));
            return;
          }
        } catch (_) {
          // ignore json parse errors, fall back to generic
        }
        throw new Error('Failed to update willingness');
      }

      // Close the modal and refresh the page or update the local state
      setIsEditModalOpen(false);

      fetchLandingPageFeed();
      handleRefreshFeedClick();
      performSearch(false);
    } catch (error) {
      console.error('Error updating willingness:', error);
      alert(t('common.error', 'An error occurred. Please try again.'));
    } finally {
      setEditModalLoading(false);
    }
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setEventData(null);
  };

  // Handle click outside to close event controls menu
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      // Check if the menu is open and the click is outside both the menu and the button
      if (
        showEventControlsMenu &&
        eventControlsMenuRef.current &&
        eventControlsMenuButtonRef.current &&
        !eventControlsMenuRef.current.contains(event.target as Node) &&
        !eventControlsMenuButtonRef.current.contains(event.target as Node)
      ) {
        setShowEventControlsMenu(false);
      }
    };

    // Only add event listener if menu is open
    if (showEventControlsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup function
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEventControlsMenu]);

  return (
    <>
      <div
        className={`search-result-card ${hasNoDetailsOrImages ? 'search-result-card--no-details' : ''} ${result.isUserOwnEvent ? 'search-result-card--user-own-event' : ''}`}
        onClick={onViewOnMap}
      >
        {result.images && result.images.length > 0 ? (
          <div className="search-result-card__image-container">
            <img
              src={result.images[0].thumbnailUrl || result.images[0].url}
              alt={`${result.address.street} ${result.address.houseNumber}`}
              className="search-result-card__image"
            />
            {result.images.length > 1 && (
              <div className="search-result-card__image-count">
                <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 3a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8zm0 1.5H5a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 .5.5h8a.5.5 0 0 0 .5-.5V5a.5.5 0 0 0-.5-.5zM2 6v5a3 3 0 0 0 3 3h6v1H5a4 4 0 0 1-4-4V6h1z"></path>
                </svg>
                <span>{result.images.length}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="search-result-card__no-image-container">
            <div className={`search-result-card__no-image ${hasNoDetailsOrImages ? 'search-result-card__no-image--no-details' : ''}`}>
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"></path>
              </svg>
            </div>
          </div>
        )}

        <div className="search-result-card__content">
          <div className="search-result-card__header-and-details-container">
            <div className="search-result-card__header">
              {isForSale ? <OffMarketSellIcon className="search-result-card__header__could-sell" /> : <OffMarketBuyIcon className="search-result-card__header__could-buy" />}
              <span className={`search-result-card__listing-type search-result-card__listing-type--${isForSale ? 'sell' : 'buy'}`}>
                {isForSale ? t('exploreListings.forSale') : t('exploreListings.buyingInterest')}
              </span>
              {/* <span className="search-result-card__date">{formatDate(result.updated_at)}</span> */}
            </div>

            <h3 className="search-result-card__address">
              {result.address.street} {result.address.houseNumber}, {result.address.city}
            </h3>

            {(result.property_size || result.property_rooms || result.property_floor) && (
              <div className="search-result-card__specs">
                {result.property_size && (
                  <span className="search-result-card__spec">
                    <strong>{result.property_size} </strong> m²
                  </span>
                )}
                {result.property_rooms && (
                  <span className="search-result-card__spec">
                    <strong>{result.property_rooms}</strong> {t('exploreListings.roomsShort')}
                  </span>
                )}
                {result.property_floor && (
                  <span className="search-result-card__spec">
                    {t('exploreListings.floorShort')} <strong>{result.property_floor}</strong>
                  </span>
                )}
              </div>)}

            {result.property_description && (
              <p className="search-result-card__description">
                {result.property_description}
              </p>
            )}

            {(result.property_has_elevator || result.property_has_balcony || result.property_renovation_status) && (
              <div className="search-result-card__features">
                {result.property_has_elevator && (
                  <span className="search-result-card__feature">
                    <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 1a1 1 0 0 1 1 1v5.585l1.293-1.292a1 1 0 0 1 1.414 1.414l-3 3a1 1 0 0 1-1.414 0l-3-3a1 1 0 0 1 1.414-1.414L7 7.585V2a1 1 0 0 1 1-1zm0 13a1 1 0 0 1-1-1V7.415l-1.293 1.292a1 1 0 0 1-1.414-1.414l3-3a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1-1.414 1.414L9 6.415V13a1 1 0 0 1-1 1z"></path>
                    </svg>
                    {t('exploreListings.elevator')}
                  </span>
                )}
                {result.property_has_balcony && (
                  <span className="search-result-card__feature">
                    <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 1v1h4v5h-1V3h-3v1a2 2 0 0 1-4 0V3H3v4H2V2h4V1a2 2 0 1 1 4 0zM2 8h12v7H2V8zm2 1.5v4h2v-4H4zm4 0v4h2v-4H8zm4 0v4h2v-4h-2z"></path>
                    </svg>
                    {t('exploreListings.balcony')}
                  </span>
                )}
                {result.property_renovation_status && (
                  <span className="search-result-card__feature">
                    <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 1l1.669.864 1.858.282.842 1.68 1.337 1.32L13.682 7l.024 1.854-1.337 1.32-.842 1.68-1.858.282L8 13l-1.669-.864-1.858-.282-.842-1.68-1.337-1.32L2.318 7l-.024-1.854 1.337-1.32.842-1.68 1.858-.282L8 1z"></path>
                    </svg>
                    {getRenovationStatusLabel(result.property_renovation_status)}
                  </span>
                )}
              </div>)}

          </div>

          <div className="search-result-card__footer-container">

            {showEventControlsMenu && (
              <div
                className="search-result-card__menu-wrapper"
                ref={eventControlsMenuRef}
              >
                <div
                  className="search-result-card__menu"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <button
                    className="search-result-card__menu__edit-button"
                    ref={editEventRef}
                    onClick={handleEditClick}
                  >
                    <div className="search-result-card__menu__edit-button__text">
                      <svg
                        height="16px"
                        viewBox="0 0 492.49284 492"
                        width="16px"
                        xmlns="http://www.w3.org/2000/svg"
                        id="fi_1828911">
                        <path
                          d="m304.140625 82.472656-270.976563 270.996094c-1.363281 1.367188-2.347656 3.09375-2.816406 4.949219l-30.035156 120.554687c-.898438 3.628906.167969 7.488282 2.816406 10.136719 2.003906 2.003906 4.734375 3.113281 7.527344 3.113281.855469 0 1.730469-.105468 2.582031-.320312l120.554688-30.039063c1.878906-.46875 3.585937-1.449219 4.949219-2.8125l271-270.976562zm0 0"></path><path d="m476.875 45.523438-30.164062-30.164063c-20.160157-20.160156-55.296876-20.140625-75.433594 0l-36.949219 36.949219 105.597656 105.597656 36.949219-36.949219c10.070312-10.066406 15.617188-23.464843 15.617188-37.714843s-5.546876-27.648438-15.617188-37.71875zm0 0"
                          >
                        </path>
                      </svg>
                      <Trans i18nKey="building.offMarket.modal.edit.menuTitle" />
                    </div>
                  </button>
                  <button
                    className="search-result-card__menu__edit-button search-result-card__menu__edit-button--warning"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowEventControlsMenu(false);
                      setShowRemoveListingDialog(result._id);
                    }}
                  >
                    <div className="search-result-card__menu__edit-button__text">
                      <TrashIcon color="#202125" size="16px" />
                      <Trans i18nKey="building.offMarket.modal.remove.menuTitle">Remove</Trans>
                    </div>
                  </button>
                </div>
              </div>
            )}

            <hr className="search-result-card__divider" />

            <div className="search-result-card__footer">
              <button
                className="search-result-card__footer__share-button"
                onClick={async (e) => {
                  e.stopPropagation();
                  e.preventDefault();

                  // Use backend share endpoint for social media crawlers to get proper meta tags
                  const shareUrl = `${process.env.REACT_APP_API_URL}/share-event?building-id=${result.building_id}&event-id=${result._id}`;

                  const shareTitle = result.could_sell
                    ? t('building.offMarket.shareTitle.couldSell')
                    : t('building.offMarket.shareTitle.couldBuy');
                  const shareText = `${shareTitle} - ${result.address.street} ${result.address.houseNumber}, ${result.address.city}`;

                  if (navigator.share) {
                    try {
                      await navigator.share({
                        title: shareTitle,
                        text: shareText,
                        url: shareUrl,
                      });
                    } catch (error) {
                      console.error('Error sharing:', error);
                    }
                  } else if (navigator.clipboard && navigator.clipboard.writeText) {
                    try {
                      await navigator.clipboard.writeText(shareUrl);
                      alert(t('shareButton.linkCopied', 'Link copied to clipboard'));
                    } catch (err) {
                      console.error('Could not copy text: ', err);
                      alert(t('shareButton.linkCopiedError', 'Could not copy link'));
                    }
                  } else {
                    // Fallback for older browsers
                    const textArea = document.createElement('textarea');
                    textArea.value = shareUrl;
                    textArea.style.position = 'fixed';
                    textArea.style.top = '0';
                    textArea.style.left = '0';
                    document.body.appendChild(textArea);
                    textArea.focus();
                    textArea.select();

                    try {
                      document.execCommand('copy');
                      alert(t('shareButton.linkCopied', 'Link copied to clipboard'));
                    } catch (err) {
                      console.error('Fallback: Unable to copy', err);
                      alert(t('shareButton.linkCopiedError', 'Could not copy link'));
                    }

                    document.body.removeChild(textArea);
                  }
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  xmlnsXlink="http://www.w3.org/1999/xlink"
                  viewBox="0,0,256,256"
                  width="16px"
                  height="16px"
                  fillRule="nonzero"
                >
                  <g
                    fill="#222222"
                    fillRule="nonzero"
                    stroke="none"
                    strokeWidth="1"
                    strokeLinecap="butt"
                    strokeLinejoin="miter"
                    strokeMiterlimit="10"
                    strokeDasharray=""
                    strokeDashoffset="0"
                    fontFamily="none"
                    fontWeight="none"
                    fontSize="none"
                    textAnchor="none"
                    style={{ mixBlendMode: 'normal' }}
                  >
                    <g transform="scale(8.53333,8.53333)">
                      <path d="M23,3c-2.20914,0 -4,1.79086 -4,4c0.00178,0.28117 0.03321,0.56136 0.09375,0.83594l-9.08203,4.54102c-0.75785,-0.87251 -1.85604,-1.3746 -3.01172,-1.37695c-2.20914,0 -4,1.79086 -4,4c0,2.20914 1.79086,4 4,4c1.15606,-0.0013 2.25501,-0.5027 3.01367,-1.375l9.07617,4.53906c-0.05923,0.27472 -0.08934,0.55491 -0.08984,0.83594c0,2.20914 1.79086,4 4,4c2.20914,0 4,-1.79086 4,-4c0,-2.20914 -1.79086,-4 -4,-4c-1.15606,0.0013 -2.25501,0.5027 -3.01367,1.375l-9.07617,-4.53906c0.05923,-0.27472 0.08934,-0.55491 0.08984,-0.83594c-0.00192,-0.28051 -0.03334,-0.56005 -0.09375,-0.83398l9.08203,-4.54102c0.75821,0.87178 1.85635,1.37313 3.01172,1.375c2.20914,0 4,-1.79086 4,-4c0,-2.20914 -1.79086,-4 -4,-4z"></path>
                    </g>
                  </g>
                </svg>
                <span className="search-result-card__footer__share-button__text">
                  <Trans i18nKey="building.controls.share" />
                </span>
              </button>

              {result.isUserOwnEvent && (
                <button
                  className="search-result-card__footer__menu-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowEventControlsMenu(!showEventControlsMenu);
                  }}
                  ref={eventControlsMenuButtonRef}
                >
                  <MiniMenuIcon color="#222" size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <OffMarketBuyAndSellDetailsModal
          isOpen={isEditModalOpen}
          onClose={handleEditModalClose}
          onConfirm={handleEditConfirm}
          actionType={result.could_sell ? 'sell' : 'buy'}
          isLoading={editModalLoading}
          isEditMode={true}
          initialData={eventData || undefined}
          willingnessId={result._id}
          setIsEditModalLoading={setEditModalLoading}
        />
      )}


    </>
  );
};

export default SearchResultCard; 